import { createMemo, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Library, Package, Sparkles } from "lucide-solid";

import { Link } from "@/lib/u-router";
import { staggerItem } from "@/lib/motion";
import { useSession } from "@/lib/auth";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useI18n } from "@/providers/i18n-provider";
import { createPowerSyncQuery } from "@famidex/shared/solid";
import {
  computeAllSeriesProgress,
  computeOverallProgress,
  getCatalogSeriesQuery,
  getCollectionQuery,
  getSeriesProgressRowsQuery,
  queryKeys,
} from "@famidex/shared";

/**
 * 🎯 Mi Colección — colección privada [L] local-first con progreso por serie.
 * Join reactivo colección local × `catalog_series` global (ambos en la DB local
 * de PowerSync, sincronizados por `my_collection` + `catalog_public`). El cálculo
 * del progreso es la función PURA `computeAllSeriesProgress`. Traducción del
 * diseño Stitch `mi_coleccion`.
 */
export function CollectionPage() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { t } = useI18n();
  const userId = session.userId;

  const stateLabel = (state: string | null): string => {
    switch (state) {
      case "wishlist": {
        return t(msg`Deseado`);
      }
      case "duplicate": {
        return t(msg`Duplicado`);
      }
      default: {
        return t(msg`En colección`);
      }
    }
  };

  const enabled = () => Boolean(drizzle() && userId());

  const items = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.collection.items(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getCollectionQuery(d, uid) : null;
    },
  });

  const progressRows = createPowerSyncQuery({
    db,
    enabled,
    queryKey: () => queryKeys.collection.progress(userId()),
    queryFn: () => {
      const d = drizzle();
      const uid = userId();
      return d && uid ? getSeriesProgressRowsQuery(d, uid) : null;
    },
  });

  const series = createPowerSyncQuery({
    db,
    enabled: () => Boolean(drizzle()),
    queryKey: () => queryKeys.catalog.series(),
    queryFn: () => {
      const d = drizzle();
      return d ? getCatalogSeriesQuery(d) : null;
    },
  });

  const seriesNameById = createMemo(() => {
    const map = new Map<string, string>();
    for (const s of series.data ?? []) {
      map.set(s.id, s.name ?? s.id);
    }
    return map;
  });

  const seriesProgress = createMemo(() => {
    const ownedBySeries: Record<string, number> = {};
    for (const row of progressRows.data ?? []) {
      if (row.series_id) {
        ownedBySeries[row.series_id] = row.owned;
      }
    }
    const totals = (series.data ?? []).map((s) => ({
      seriesId: s.id,
      total: s.total_items ?? 0,
    }));
    return computeAllSeriesProgress(totals, ownedBySeries).filter((p) => p.owned > 0);
  });

  const overall = createMemo(() => computeOverallProgress(seriesProgress()));

  const summaryLabel = () => {
    const o = overall();
    const owned = o.owned;
    const total = o.total || "?";
    const completed = o.completedSeries;
    return t(msg`${owned} de ${total} figuras · ${completed} series completadas`);
  };

  return (
    <div class="flex flex-col gap-6" data-testid="collection-page">
      <header class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-foreground">{t(msg`Mi colección`)}</h1>
          <p class="mt-1 text-sm text-muted-foreground">{summaryLabel()}</p>
        </div>
        <Link
          to="/catalog"
          class="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
        >
          <Library class="h-4 w-4" />
          {t(msg`Catálogo`)}
        </Link>
      </header>

      {/* Progreso global */}
      <section class="rounded-xl border border-border/60 bg-card/70 p-5">
        <div class="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-wide text-primary">
          <span>{t(msg`Progreso global`)}</span>
          <span>{overall().percent}%</span>
        </div>
        <div class="h-3 w-full overflow-hidden rounded-full bg-secondary">
          <div
            class="h-full rounded-full bg-primary transition-all duration-700"
            style={{ width: `${overall().percent}%` }}
          />
        </div>
      </section>

      {/* Progreso por serie */}
      <Show when={seriesProgress().length > 0}>
        <section class="flex flex-col gap-3">
          <h2 class="text-sm font-bold uppercase tracking-wide text-muted-foreground">
            {t(msg`Progreso por serie`)}
          </h2>
          <For each={seriesProgress()}>
            {(p, index) => (
              <div
                ref={(el: HTMLDivElement) => staggerItem(el, index())}
                class="rounded-lg border border-border/50 bg-card/50 p-4"
              >
                <div class="mb-1.5 flex items-center justify-between text-sm">
                  <span class="font-medium text-foreground">
                    {seriesNameById().get(p.seriesId) ?? p.seriesId}
                  </span>
                  <span class="flex items-center gap-1 text-xs text-muted-foreground">
                    <Show when={p.complete}>
                      <Sparkles class="h-3.5 w-3.5 text-primary" />
                    </Show>
                    {p.owned}/{p.total || "?"}
                  </span>
                </div>
                <div class="h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div
                    class="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${p.percent}%` }}
                  />
                </div>
              </div>
            )}
          </For>
        </section>
      </Show>

      {/* Ítems */}
      <section>
        <h2 class="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">
          {t(msg`Mis figuras`)}
        </h2>
        <Show
          when={(items.data ?? []).length > 0}
          fallback={
            <div
              data-testid="collection-empty"
              class="grid place-items-center gap-2 rounded-xl border border-dashed border-border/70 bg-card/40 py-14 text-center text-muted-foreground"
            >
              <Package class="h-8 w-8" />
              <p class="text-sm">{t(msg`Tu colección está vacía.`)}</p>
              <Link
                to="/scanner"
                class="mt-1 rounded-full bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground"
              >
                {t(msg`Escanea tu primer set`)}
              </Link>
            </div>
          }
        >
          <ul class="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <For each={items.data}>
              {(item, index) => (
                <li
                  data-testid="collection-item"
                  ref={(el: HTMLLIElement) => staggerItem(el, index())}
                  class="flex flex-col overflow-hidden rounded-xl border border-border/60 bg-card"
                >
                  <div class="relative aspect-square bg-secondary">
                    <Show
                      when={item.image_url}
                      fallback={
                        <div class="grid h-full w-full place-items-center text-muted-foreground/50">
                          <Package class="h-8 w-8" />
                        </div>
                      }
                    >
                      <img
                        src={item.image_url ?? ""}
                        alt={item.edition_name ?? ""}
                        loading="lazy"
                        class="h-full w-full object-cover"
                      />
                    </Show>
                    <Show when={(item.quantity ?? 1) > 1}>
                      <span class="absolute right-2 top-2 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground">
                        ×{item.quantity}
                      </span>
                    </Show>
                  </div>
                  <div class="flex flex-1 flex-col gap-1 p-3">
                    <h3 class="line-clamp-2 text-sm font-semibold leading-tight text-foreground">
                      {item.edition_name ?? t(msg`Producto`)}
                    </h3>
                    <span class="mt-auto w-fit rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent-foreground">
                      {stateLabel(item.state)}
                    </span>
                  </div>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </section>
    </div>
  );
}
